import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  FiMenu, 
  FiHome, 
  FiUsers, 
  FiFileText, 
  FiPackage, 
  FiCreditCard, 
  FiBarChart, 
  FiSettings,
  FiMoon,
  FiSun,
  FiSearch,
  FiCode
} from 'react-icons/fi';
import { ReactComponent as SedraLogo } from '../assets/logo-color.svg';
import { ClosedBetaButton } from './beta/ClosedBetaButton';
import { ClosedBetaWizard } from './beta/ClosedBetaWizard';

interface Business {
  id: string;
  name: string;
  brand_color?: string;
  nickname?: string | null;
}

interface LayoutProps {
  children: React.ReactNode;
  currentBusiness: Business | null;
  onBusinessChange: (businessId: string) => void;
  businesses: Business[];
  darkMode: boolean;
  onDarkModeToggle: () => void;
}

const drawerWidth = 240;

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  disabled?: boolean;
  tooltip?: string;
}

const getMenuItems = (isDev: boolean = false): MenuItem[] => {
  const baseItems = [
    { text: 'Dashboard', icon: <FiHome />, path: '/dashboard' },
    { text: 'Customers', icon: <FiUsers />, path: '/customers' },
    { text: 'Invoices', icon: <FiFileText />, path: '/invoices' },
    { text: 'Estimates', icon: <FiFileText />, path: '/estimates' },
    { text: 'Items/Pricing', icon: <FiPackage />, path: '/items' },
    { text: 'Expenses', icon: <FiCreditCard />, path: '/expenses' },
    { text: 'Reports', icon: <FiBarChart />, path: '/reports', disabled: true, tooltip: 'Disabled during beta testing!' },
    { text: 'Settings', icon: <FiSettings />, path: '/settings' },
    { text: 'Manage Customer Portal', icon: <FiCode />, path: '/portal-admin' },
  ];

  if (isDev) {
    baseItems.push(
      { text: 'Developer Portal', icon: <FiCode />, path: '/admin/feedback' }
    );
  }

  return baseItems;
};

const getEntityTypeFromPath = (pathname: string): 'invoices' | 'customers' | 'items' => {
  if (pathname.startsWith('/invoices') || pathname.startsWith('/estimates')) return 'invoices';
  if (pathname.startsWith('/customers')) return 'customers';
  if (pathname.startsWith('/items')) return 'items';
  return 'invoices'; // default
};

export default function Layout({ 
  children,
  currentBusiness,
  onBusinessChange,
  businesses,
  darkMode,
  onDarkModeToggle
}: LayoutProps) {
  const { API_BASE_URL, logout, user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [addBusinessOpen, setAddBusinessOpen] = useState(false);
  const [newBusiness, setNewBusiness] = useState({
    name: '',
    logo_url: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    phone: '',
    email: '',
    website: '',
    nickname: '' as string | undefined,
  });
  const [addingBusiness, setAddingBusiness] = useState(false);
  const [addBusinessError, setAddBusinessError] = useState('');
  const [betaWizardOpen, setBetaWizardOpen] = useState(false);

  const showDevBanner = (process.env.REACT_APP_DEV_MODE === '1') || (process.env.NODE_ENV !== 'production');

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleAddBusiness = async () => {
    setAddingBusiness(true);
    setAddBusinessError('');
    const { nickname, ...rest } = newBusiness as any;
    const businessToSend = { ...rest };
    try {
      const res = await fetch(`${API_BASE_URL}/businesses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(businessToSend)
      });
      if (!res.ok) {
        const err = await res.json();
        setAddBusinessError(err.error || 'Failed to add business');
        setAddingBusiness(false);
        return;
      }
      const business = await res.json();
      if (nickname) {
        try {
          await fetch(`${API_BASE_URL}/businesses/${business.id}/nickname`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ nickname })
          });
        } catch (_) {}
      }
      setAddBusinessOpen(false);
      setNewBusiness({
        name: '', logo_url: '', street: '', city: '', state: '', zip: '', country: '', phone: '', email: '', website: '', nickname: ''
      });
      setAddingBusiness(false);
      setAddBusinessError('');
      window.location.reload();
    } catch (e) {
      setAddBusinessError('Failed to add business');
      setAddingBusiness(false);
    }
  };

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (mobileOpen) {
      const previous = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = previous; };
    }
  }, [mobileOpen]);

  // Close on Escape when mobile drawer is open
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  const drawer = (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-gray-100 dark:border-gray-800">
        <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100 leading-none">
          <SedraLogo className="block h-6 w-auto" aria-label="Sedra" />
          <span className="brand-invoice brand-pill align-middle">Invoice</span>
        </h1>
      </div>
      <nav aria-label="Primary" className="mt-2 flex-1 px-2 py-2 scroll-smoothy">
        {getMenuItems(user?.is_dev).map((item) => (
          <div key={item.text} className="relative group">
            <button
              type="button"
              onClick={() => !item.disabled && handleNavigation(item.path)}
              disabled={item.disabled}
              aria-current={location.pathname === item.path ? 'page' : undefined}
              className={`w-full flex items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-medium transition-colors ${
                item.disabled
                  ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  : (location.pathname === item.path)
                    ? 'bg-sedra/10 text-sedra ring-1 ring-sedra/20 dark:bg-sedra/20 dark:text-sedra'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white'
              }`}
            >
              <span className={`${location.pathname === item.path ? 'text-sedra dark:text-sedra' : 'text-gray-500 dark:text-gray-400'} text-lg`}>{item.icon}</span>
              <span className="text-sm font-medium">{item.text}</span>
            </button>
            {item.disabled && item.tooltip && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                {item.tooltip}
                <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-0 h-0 border-t-4 border-b-4 border-l-4 border-transparent border-r-4 border-gray-900 dark:border-gray-100"></div>
              </div>
            )}
          </div>
        ))}
      </nav>
      
      {/* Beta Portal Button - Fixed at bottom */}
      <div className="px-2 py-2 border-t border-gray-100 dark:border-gray-800">
        <ClosedBetaButton onClick={() => setBetaWizardOpen(true)} />
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-sidebar md:flex-col bg-[#4a274f0F] border-r border-sedra/10 dark:bg-gray-900 dark:border-gray-800">
        {drawer}
      </aside>

      {/* Mobile sidebar drawer */}
      <div className={`md:hidden fixed inset-y-0 left-0 z-50 w-sidebar bg-white dark:bg-gray-900 border-r border-gray-100 dark:border-gray-800 transform transition-transform duration-300 ease-in-out ${
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {drawer}
      </div>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/25 md:hidden"
          aria-hidden={!mobileOpen}
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Dev banner */}
        {showDevBanner && (
          <div className="w-full bg-red-600 text-white text-center text-xs sm:text-sm py-1 z-50">
            DEVELOPMENT MODE ENABLED
          </div>
        )}
        {/* Skip link */}
        <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:rounded-md focus:bg-white focus:px-3 focus:py-2 focus:text-sedra focus:shadow">
          Skip to content
        </a>
        {/* Top bar */}
        <header className="sticky top-0 z-40 bg-[#4a274f0A] backdrop-blur supports-[backdrop-filter]:bg-[#4a274f0A] border-b border-sedra/10 dark:bg-gray-900/70 dark:border-gray-800">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-header flex items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={handleDrawerToggle}
                className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra md:hidden dark:text-neutral-300 dark:hover:bg-neutral-800 dark:hover:text-neutral-100 dark:focus:ring-offset-neutral-900"
              >
                <FiMenu className="w-5 h-5" />
              </button>
              
              {/* Business Switcher */}
              <div className="relative">
                <select
                  value={currentBusiness?.id || ''}
                  onChange={(e) => { const v = e.target.value; if (v) onBusinessChange(v); }}
                  className="rounded-xl border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 dark:focus:ring-offset-neutral-900"
                >
                  <option value="">Select Business</option>
                  {businesses.map((business) => (
                    <option key={business.id} value={business.id}>
                      {business.name}{business.nickname ? ` (${business.nickname})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              
              <button
                type="button"
                onClick={() => setAddBusinessOpen(true)}
                className="rounded-xl bg-sedra px-3.5 py-2 text-sm font-semibold text-white hover:bg-sedra-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra"
              >
                Add Business
              </button>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onDarkModeToggle}
                className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra dark:text-neutral-300 dark:hover:bg-neutral-800 dark:hover:text-neutral-100 dark:focus:ring-offset-neutral-900"
              >
                {darkMode ? <FiSun className="w-5 h-5" /> : <FiMoon className="w-5 h-5" />}
              </button>
              
              <button
                type="button"
                onClick={() => {
                  logout();
                  navigate('/login');
                }}
                className="rounded-xl border border-gray-300 bg-white px-3.5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                Logout
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main id="main" className="flex-1 overflow-auto p-6">
          {children}
        </main>
      </div>

      {/* Add Business Modal */}
      {addBusinessOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">Add New Business</h2>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Business Name"
                value={newBusiness.name}
                onChange={e => setNewBusiness({ ...newBusiness, name: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                disabled={addingBusiness}
                required
              />
              <input
                type="text"
                placeholder="Logo URL"
                value={newBusiness.logo_url}
                onChange={e => setNewBusiness({ ...newBusiness, logo_url: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                disabled={addingBusiness}
              />
              <input
                type="text"
                placeholder="Street"
                value={newBusiness.street}
                onChange={e => setNewBusiness({ ...newBusiness, street: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                disabled={addingBusiness}
              />
              <div className="grid grid-cols-3 gap-2">
                <input
                  type="text"
                  placeholder="City"
                  value={newBusiness.city}
                  onChange={e => setNewBusiness({ ...newBusiness, city: e.target.value })}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  disabled={addingBusiness}
                />
                <input
                  type="text"
                  placeholder="State"
                  value={newBusiness.state}
                  onChange={e => setNewBusiness({ ...newBusiness, state: e.target.value })}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  disabled={addingBusiness}
                />
                <input
                  type="text"
                  placeholder="ZIP"
                  value={newBusiness.zip}
                  onChange={e => setNewBusiness({ ...newBusiness, zip: e.target.value })}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  disabled={addingBusiness}
                />
              </div>
              <input
                type="text"
                placeholder="Country"
                value={newBusiness.country}
                onChange={e => setNewBusiness({ ...newBusiness, country: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                disabled={addingBusiness}
              />
              <input
                type="text"
                placeholder="Phone"
                value={newBusiness.phone}
                onChange={e => setNewBusiness({ ...newBusiness, phone: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                disabled={addingBusiness}
              />
              <input
                type="email"
                placeholder="Email"
                value={newBusiness.email}
                onChange={e => setNewBusiness({ ...newBusiness, email: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                disabled={addingBusiness}
              />
              <input
                type="text"
                placeholder="Website"
                value={newBusiness.website}
                onChange={e => setNewBusiness({ ...newBusiness, website: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                disabled={addingBusiness}
              />
              <input
                type="text"
                placeholder="Nickname (private, optional)"
                value={newBusiness.nickname || ''}
                onChange={e => setNewBusiness({ ...newBusiness, nickname: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                disabled={addingBusiness}
              />
              {addBusinessError && <div className="text-red-600 text-sm">{addBusinessError}</div>}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setAddBusinessOpen(false)}
                disabled={addingBusiness}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddBusiness}
                disabled={!newBusiness.name || addingBusiness}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Beta Feedback Wizard Modal */}
      <ClosedBetaWizard 
        isOpen={betaWizardOpen} 
        onClose={() => setBetaWizardOpen(false)} 
      />
    </div>
  );
}; 